import React from 'react';
import { getStatusBadge, getLeaveTypeLabel } from './leaveHelpers.jsx';

// Leave dates are saved from "YYYY-MM-DD" strings (UTC midnight), so format
// them in UTC to show exactly the day the employee picked.
const fmtLeaveDay = (d) => {
  const date = new Date(d);
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    day: date.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'UTC' }),
    month: date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }),
    key: date.toISOString().slice(0, 10)
  };
};

// "Today" / "Tomorrow" / "in 3 days" / "2 days ago" relative to the local date
const relativeDay = (key) => {
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((new Date(`${key}T00:00:00Z`) - todayUtc) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return diff > 0 ? `in ${diff} days` : `${-diff} days ago`;
};

const timeAgo = (d) => {
  const mins = Math.floor((Date.now() - new Date(d)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const initials = (e) => `${e?.firstName?.[0] || ''}${e?.lastName?.[0] || ''}`.toUpperCase() || '?';

/**
 * Single leave-request card, memoized and used as a virtualized row.
 * `onReview(leave, action)` opens the approve/reject modal.
 * Classes are prefixed `lr-` so generic names from other pages' CSS
 * (.info-row, .card-header, .employee-info …) can't break the layout.
 */
function LeaveRequestRow({ leave, onReview }) {
  const employee = leave.employee || {};
  const dates = [...(leave.leaveDates || [])]
    .sort((a, b) => new Date(a) - new Date(b))
    .map(fmtLeaveDay);
  const days = dates.length || leave.leaveCount || 1;
  const isPending = leave.status === 'pending';

  return (
    <div className={`lr-card lr-${leave.status || 'pending'}`}>
      <div className="lr-main">
        <div className="lr-person">
          {employee.profilePicture ? (
            <img className="lr-avatar" src={employee.profilePicture} alt="" />
          ) : (
            <div className="lr-avatar">{initials(employee)}</div>
          )}
          <div className="lr-person-text">
            <div className="lr-name">{employee.firstName} {employee.lastName}</div>
            <div className="lr-sub">{getLeaveTypeLabel(leave.leaveType)} · {timeAgo(leave.createdAt)}</div>
          </div>
        </div>

        {/* The days being requested — the most important part */}
        <div className="lr-dates" aria-label={`${days} day leave`}>
          {dates.length > 0 ? dates.map((d) => (
            <span className="lr-date-chip" key={d.key} title={relativeDay(d.key)}>
              <strong>{d.weekday}, {d.month} {d.day}</strong>
              <span className="lr-date-rel">{relativeDay(d.key)}</span>
            </span>
          )) : (
            <span className="lr-no-dates">{days} {days === 1 ? 'day' : 'days'} · dates not selected</span>
          )}
        </div>

        <div className="lr-side">
          {isPending ? (
            <div className="lr-actions">
              <button className="lr-btn lr-btn-reject" onClick={() => onReview(leave, 'rejected')} title="Reject">
                ✕<span className="lr-btn-text"> Reject</span>
              </button>
              <button className="lr-btn lr-btn-approve" onClick={() => onReview(leave, 'approved')} title="Approve">
                ✓<span className="lr-btn-text"> Approve</span>
              </button>
            </div>
          ) : (
            <>
              {leave.status === 'approved' && leave.leavePaymentStatus && leave.leavePaymentStatus !== 'pending' && (
                <span className={`lr-chip lr-pay-${leave.leavePaymentStatus}`}>
                  {leave.leavePaymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                </span>
              )}
              {getStatusBadge(leave.status)}
            </>
          )}
        </div>
      </div>

      {(leave.reason || leave.reviewedBy || leave.rejectionReason) && (
        <div className="lr-foot">
          {leave.reason && <span className="lr-reason" title={leave.reason}>“{leave.reason}”</span>}
          {leave.rejectionReason && (
            <span className="lr-rejection" title={leave.rejectionReason}>Rejected: {leave.rejectionReason}</span>
          )}
          {leave.reviewedBy && (
            <span className="lr-reviewed">
              by {leave.reviewedBy.firstName} {leave.reviewedBy.lastName}
              {leave.reviewedAt && ` · ${new Date(leave.reviewedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(LeaveRequestRow);
